import './styles.scss';

import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

import { Text, Button } from "@fragments";
import { AddRounded, SettingsRounded, PlayCircleOutlineRounded, SkipNextRounded, AccessTimeRounded } from "@mui/icons-material";
import { SlowMotionVideoRounded } from "@material-ui/icons";
import { IconArrowsSort, IconStackForward  } from "@tabler/icons-react";

import VideoCard from "../VideoCard";
import { UserAvatar } from "@components/Community";

function ModuleBanner({ banner }) {
    if (!banner || banner === '') return <></>;
    return (
        <div className="module-banner">
            <img className="module-banner-main" src={banner} alt="" />
            <img className="effect-01" src={banner} alt="" />
        </div>
    );
}

function formatDuration(seconds) {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function ProgressBar({ watched, total }) {
    const percentage = total > 0 ? Math.min((watched / total) * 100, 100) : 0;
    return (
        <div className="playlist-progress">
            <Text.span
                value={`Progresso - ${watched}/${total} vídeos`}
                contrast={5}
                className="playlist-progress-label"
            />
            <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${percentage}%` }} />
            </div>
        </div>
    );
}

function NextVideoCard({ content, nextVideo, nextVideoIndex, handleOpenContent }) {
    if (!nextVideo) return null;

    const duration = formatDuration(nextVideo.video?.duration);

    return (
        <div
            className="playlist-next-video"
            onClick={() => handleOpenContent(content, nextVideoIndex)}
        >
            <div className="next-video-thumbnail" style={{ aspectRatio: nextVideo.video?.aspect_ratio || '16/9' }}>
                {nextVideo.banner
                    ? <img src={nextVideo.banner} alt={nextVideo.name} />
                    : <div className="next-video-thumbnail-placeholder" />
                }
                <div className="next-video-play-overlay">
                    <PlayCircleOutlineRounded className="next-video-play-icon" />
                </div>
            </div>
            <div className="next-video-info">
                <Text.small
                    value={`Assista em sequência · ${duration}`}
                    contrast={4}
                    weight={300}
                    className="next-video-label"
                />
                <Text.h5
                    value={nextVideo.name}
                    className="next-video-title"
                    maxLines={2}
                />
                {nextVideo.creator?.id && (
                    <div className="next-video-creator">
                        <UserAvatar size={18} user={nextVideo.creator} displayName={true} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Playlist({
    content,
    course,
    handleOpenContent,
    handleEdit,
    handleReorderContents,
    handleAddContent,
    handleEditContent,
}) {
    const calculateResponsiveItems = () => {
        const defaultWidth = 170;
        const lessonWidths = content.contents?.length > 0
            ? content.contents?.map(lesson => lesson.video?.aspect_ratio > 1 ? 300 : 170)
            : [defaultWidth];
        const averageWidth = lessonWidths.reduce((sum, width) => sum + width, 0) / lessonWidths.length;
        const gap = 12;
        const containerWidths = {
            superLargeDesktop: 1600 - 200,
            desktop: 1024 - 200,
            tablet: 768 - 200,
            mobile: 768 - 100,
        };
        const calculateItems = (containerWidth) => {
            const availableWidth = containerWidth - gap;
            const itemsThatFit = Math.floor(availableWidth / (averageWidth + gap));
            return Math.max(1, Math.min(itemsThatFit, lessonWidths.length));
        };
        return {
            superLargeDesktop: { breakpoint: { max: 4000, min: 1600 }, items: calculateItems(containerWidths.superLargeDesktop) },
            desktop: { breakpoint: { max: 1600, min: 1024 }, items: calculateItems(containerWidths.desktop) },
            tablet: { breakpoint: { max: 1024, min: 768 }, items: calculateItems(containerWidths.tablet) },
            mobile: { breakpoint: { max: 768, min: 0 }, items: calculateItems(containerWidths.mobile) },
        };
    };

    const responsive = calculateResponsiveItems();

    const totalDurationSeconds = content.contents?.reduce((sum, c) => sum + (c.video?.duration || 0), 0) || 0;
    const totalMinutes = Math.round(totalDurationSeconds / 60);
    const videosCount = content.contents?.length || 0;

    const watchedCount = content.progress?.watched ?? 0;
    const videosTotal = content.progress?.videos_total ?? videosCount;

    const nextVideoIndex = content.contents?.findIndex(c => !c.watched) ?? -1;
    const nextVideo = nextVideoIndex >= 0 ? content.contents[nextVideoIndex] : null;

    return (
        <div className="playlist-container">
            <div className="playlist-header">
                <div className="playlist-header-main">
                    <ModuleBanner banner={content.banner} />
                    <div className="playlist-header-info">
                        <div className="playlist-stats">
                            <Text.span
                                left={<SlowMotionVideoRounded />}
                                value={`PLAYLIST`}
                                contrast={8}
                                className="playlist-badge"
                            />

                            <Text.span
                                left={<SkipNextRounded />}
                                value={`${videosCount} vídeo${videosCount !== 1 ? 's' : ''}`}
                                contrast={5}
                            />
                            <Text.span
                                left={<AccessTimeRounded />}
                                value={`${totalMinutes} minuto${totalMinutes !== 1 ? 's' : ''}`}
                                contrast={5}
                            />
                            {handleEdit && (
                                <Button
                                    color="inverse"
                                    onClick={() => handleEdit(content)}
                                    startIcon={<SettingsRounded />}
                                    size="small"
                                    text="Configurar"
                                />
                            )}
                            {handleReorderContents && (
                                <Button
                                    color="overlay"
                                    onClick={() => handleReorderContents(content.id)}
                                    startIcon={<IconStackForward />}
                                    size="small"
                                    text="Reordenar conteúdos"
                                    disabled={videosCount < 2}
                                    tooltip={videosCount < 2 ? "Adicione ao menos dois conteúdos para reordenar" : undefined}
                                />
                            )}
                        </div>
                        <Text.h2
                            value={content.name}
                            className="playlist-name"
                        />
                        <Text.h4
                            value={content.description}
                            className="playlist-description"
                            maxLines={2}
                            contrast={6}
                            grow={false}
                        />
                        <ProgressBar watched={watchedCount} total={videosTotal} />
                    </div>
                </div>

                <div className="playlist-header-side">
                    <NextVideoCard
                        content={content}
                        nextVideo={nextVideo}
                        nextVideoIndex={nextVideoIndex}
                        handleOpenContent={handleOpenContent}
                    />
                </div>
            </div>

            <Carousel
                responsive={responsive}
                infinite={false}
                draggable={true}
                swipeable={true}
                keyBoardControl={true}
                containerClass="playlist pb-1"
                itemClass="lesson-container"
            >
                {handleAddContent && (
                    <div
                        key={`lesson-add`}
                        className="lesson-add"
                        onClick={() => handleAddContent({ playlist: content.id, course })}
                    >
                        <AddRounded className="play-lesson" />
                    </div>
                )}
                {content.contents?.map((lesson, i) => (
                    <VideoCard
                        key={`lesson-${i}`}
                        lesson={lesson}
                        onClick={() => handleOpenContent(content, i)}
                        onEdit={handleEditContent}
                    />
                ))}
            </Carousel>
        </div>
    );
}
